// Sales page for the guided "Build with AI" course — /build-india (region via
// prop, config in buildCourseConfig.ts). Guidance-first pitch: the course
// isn't the product, finishing is. Flow: pricing card → order form (name +
// email + WhatsApp + tier) → POST /api/webinar-register (slug build-india,
// segment = tier, UTMs saved BEFORE payment so abandoners become a follow-up
// list) → Razorpay payment link → /build-india/thanks fires the Purchase
// pixel. Empty payment link = test mode (straight to thanks, no Purchase).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BUILD_REGIONS, BuildRegionKey, BuildTierKey } from './buildCourseConfig';

// Social/credibility links — founder LinkedIn only on this page; company
// handles are also mirrored in the HomePage footer.
const SOCIAL = {
  founderLinkedin: 'https://www.linkedin.com/in/francis7a/',
  corestratLinkedin: 'https://www.linkedin.com/company/103316827/',
  corestratInstagram: 'https://www.instagram.com/corestratai/',
  cubemateInstagram: 'https://www.instagram.com/cubemate_ai/',
};

const LinkedInIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="#0A66C2">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const InstagramIcon: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <svg className={className} viewBox="0 0 24 24">
    <defs>
      <radialGradient id="igGradient" cx="30%" cy="107%" r="150%">
        <stop offset="0%" stopColor="#fdf497" />
        <stop offset="5%" stopColor="#fdf497" />
        <stop offset="45%" stopColor="#fd5949" />
        <stop offset="60%" stopColor="#d6249f" />
        <stop offset="90%" stopColor="#285AEB" />
      </radialGradient>
    </defs>
    <path fill="url(#igGradient)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const BuildCoursePage: React.FC<{ region: BuildRegionKey }> = ({ region }) => {
  const cfg = BUILD_REGIONS[region];
  const navigate = useNavigate();
  const [tier, setTier] = useState<BuildTierKey>('professional');
  const [form, setForm] = useState({ name: '', email: '', whatsapp: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  // Enquiry lead-magnet form ("send me the project list on WhatsApp") — captures
  // contact info from visitors who aren't ready to pay yet
  const [enquiry, setEnquiry] = useState({ name: '', email: '', whatsapp: '' });
  const [enquiryStatus, setEnquiryStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [enquiryError, setEnquiryError] = useState('');

  // Track UTM-tagged ad visits — same pattern as /us and /webinar-aug
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUtm = params.get('utm_source') || params.get('gclid');
    if (!hasUtm) return;
    const apiUrl = process.env.REACT_APP_API_URL || '';
    fetch(`${apiUrl}/api/ad-visit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        landing_page: cfg.path,
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_content: params.get('utm_content') || undefined,
        utm_term: params.get('utm_term') || undefined,
        gclid: params.get('gclid') || undefined,
        referrer: document.referrer || undefined,
      }),
    })
      .then(r => r.json())
      .then(data => {
        if (data.visit_id) localStorage.setItem('ad_visit_id', String(data.visit_id));
      })
      .catch(() => { /* best-effort */ });
  }, [cfg.path]);

  const chooseTier = (t: BuildTierKey) => {
    setTier(t);
    document.getElementById('join')?.scrollIntoView({ behavior: 'smooth' });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg('');
    const params = new URLSearchParams(window.location.search);
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const selected = cfg.tiers[tier];
    try {
      const res = await fetch(`${apiUrl}/api/webinar-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          webinar_slug: cfg.slug,
          name: form.name,
          email: form.email,
          whatsapp: form.whatsapp,
          segment: tier,
          utm_source: params.get('utm_source') || undefined,
          utm_medium: params.get('utm_medium') || undefined,
          utm_campaign: params.get('utm_campaign') || undefined,
          utm_content: params.get('utm_content') || undefined,
          utm_term: params.get('utm_term') || undefined,
          referrer: document.referrer || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || 'Something went wrong. Please try again.');
      }
      localStorage.setItem('build_reg_id', String(data.registration_id));
      localStorage.setItem('build_tier', tier);
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: `${cfg.slug}_order_form` });
        window.fbq('track', 'InitiateCheckout', {
          content_name: `${cfg.slug}_${tier}`,
          value: selected.price,
          currency: cfg.currency,
        });
      }
      if (cfg.googleAdsConversion && typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', { send_to: cfg.googleAdsConversion });
      }
      if (selected.paymentLink) {
        window.location.href = selected.paymentLink;
      } else {
        navigate(`${cfg.path}/thanks`);
      }
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message || 'Something went wrong. Please try again.');
    }
  };

  // Enquiry form: captures name + email + WhatsApp server-side BEFORE opening
  // WhatsApp, so we keep the contact info even if they never send the message.
  // Own slug (`${cfg.slug}-enquiry`) — the register endpoint dedupes on
  // slug+email, so sharing the order slug would swallow the tier of anyone who
  // enquired first and paid later.
  const enquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (enquiryStatus === 'submitting') return;
    setEnquiryStatus('submitting');
    setEnquiryError('');
    const params = new URLSearchParams(window.location.search);
    const apiUrl = process.env.REACT_APP_API_URL || '';
    try {
      const res = await fetch(`${apiUrl}/api/webinar-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          webinar_slug: `${cfg.slug}-enquiry`,
          name: enquiry.name,
          email: enquiry.email,
          whatsapp: enquiry.whatsapp,
          segment: 'enquiry',
          utm_source: params.get('utm_source') || undefined,
          utm_medium: params.get('utm_medium') || undefined,
          utm_campaign: params.get('utm_campaign') || undefined,
          utm_content: params.get('utm_content') || undefined,
          utm_term: params.get('utm_term') || undefined,
          referrer: document.referrer || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || 'Something went wrong. Please try again.');
      }
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: `${cfg.slug}_enquiry_form` });
      }
      setEnquiryStatus('done');
      // Mobile: open the chat with a prefilled message so the conversation
      // starts immediately. Desktop: many visitors have no WhatsApp Web, so we
      // just confirm and Francis messages them instead (number is saved either way).
      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (isMobile && cfg.supportWhatsappNumber) {
        window.location.href = `https://wa.me/${cfg.supportWhatsappNumber}?text=${encodeURIComponent(
          `Hi, I'm ${enquiry.name} — please send me the full project list for the AI course`
        )}`;
      }
    } catch (err: any) {
      setEnquiryStatus('error');
      setEnquiryError(err?.message || 'Something went wrong. Please try again.');
    }
  };

  // Meta Contact event on every direct WhatsApp click (hero, pricing, bubble)
  const trackContact = () => {
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Contact', { content_name: `${cfg.slug}_whatsapp` });
    }
  };

  const startLine =
    cfg.mode === 'cohort' && cfg.cohortStartDate
      ? `Next batch starts ${cfg.cohortStartDate}`
      : 'Join anytime — start building the same day';

  const whatsappUrl = cfg.supportWhatsappNumber
    ? `https://wa.me/${cfg.supportWhatsappNumber}?text=${encodeURIComponent('Hi, I have a question about the Build with AI course')}`
    : '';

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden">
      {/* ── Navigation ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="/cubemate-logo.png" alt="Cubemate" className="w-9 h-9 rounded-lg" />
              <span className="text-xl font-bold text-gray-900">Cubemate</span>
            </div>
            <button
              onClick={() => chooseTier('professional')}
              className="px-5 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-all shadow-sm"
            >
              Join the course
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-3xl mx-auto">
          <span className="inline-block px-4 py-1.5 rounded-full bg-violet-50 text-violet-700 text-sm font-semibold mb-6">
            ✨ Experience a new way of learning and building with AI
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight mb-6">
            A course you'll actually finish.{' '}
            <span className="text-violet-600">Because you're never stuck alone.</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-4">
            Build {cfg.tiers.professional.projects} real AI projects, every one running live in your browser —
            with 1-on-1 mentor sessions and fast doubt support behind you.
          </p>
          <p className="text-gray-500 mb-10">{startLine}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => chooseTier('professional')}
              className="px-8 py-4 text-lg font-bold rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition-all shadow-lg shadow-violet-600/25 hover:scale-105 transform"
            >
              Start building — {cfg.tiers.professional.priceDisplay}
            </button>
            {whatsappUrl && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={trackContact}
                className="px-8 py-4 text-lg font-bold rounded-xl border-2 border-green-600 text-green-700 hover:bg-green-50 transition-all"
              >
                💬 Ask us on WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      {/* ── Why courses don't get finished ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            You've bought courses before. <span className="text-violet-600">You know how it goes.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10 text-left">
            {[
              ['📦', 'Day 1', 'You enrol, motivated. The dashboard shows 60 videos.'],
              ['🕳️', 'Day 9', 'You hit a doubt nobody answers. One missed evening becomes a week.'],
              ['🪦', 'Day 30', 'Video 7 of 60. Another course you paid for and never finished.'],
            ].map(([icon, day, text]) => (
              <div key={day} className="p-6 rounded-2xl border border-gray-200 bg-white">
                <div className="text-3xl mb-3">{icon}</div>
                <p className="text-sm font-semibold text-violet-600 uppercase tracking-wide mb-2">{day}</p>
                <p className="text-gray-600">{text}</p>
              </div>
            ))}
          </div>
          <p className="text-xl sm:text-2xl font-bold mt-10">
            The course isn't the hard part. <span className="text-violet-600">Finishing is.</span> That's what this
            one is built around.
          </p>
        </div>
      </section>

      {/* ── What you'll build ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">
            One real project at a time — <span className="text-violet-600">not 60 videos</span>
          </h2>
          <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
            {cfg.tiers.professional.projects} guided projects on Professional, all {cfg.tiers.advance.projects} on
            Advance. You build each one hands-on — most right in your browser, some in VS Code — and every
            project you finish is yours to keep.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cfg.sampleProjects.map((p) => (
              <div key={p.name} className="p-6 rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all">
                <div className="text-3xl mb-3">{p.icon}</div>
                <h3 className="text-lg font-bold mb-2">{p.name}</h3>
                <p className="text-gray-600 text-sm">{p.blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Enquiry lead magnet — capture contact info from not-ready-to-pay visitors ── */}
      {cfg.supportWhatsappNumber && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-violet-600" id="curriculum">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Want the full project list first?
            </h2>
            <p className="text-violet-100 mb-8">
              Enter your WhatsApp number and we'll send you the complete curriculum — and answer any
              question you have. No payment needed.
            </p>
            {enquiryStatus === 'done' ? (
              <div className="p-6 rounded-2xl bg-white text-left sm:text-center">
                <p className="font-bold text-lg mb-1">✅ Got it, {enquiry.name}!</p>
                <p className="text-gray-600 text-sm">
                  We'll message you on WhatsApp with the full project list shortly. Can't wait?{' '}
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={trackContact} className="text-green-600 font-semibold hover:underline">
                    Message us first
                  </a>
                  .
                </p>
              </div>
            ) : (
              <form onSubmit={enquirySubmit} className="p-6 rounded-2xl bg-white space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    required
                    placeholder="Your name"
                    value={enquiry.name}
                    onChange={(e) => setEnquiry({ ...enquiry, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-violet-600 focus:ring-1 focus:ring-violet-600 outline-none"
                  />
                  <input
                    type="tel"
                    required
                    placeholder="WhatsApp number"
                    value={enquiry.whatsapp}
                    onChange={(e) => setEnquiry({ ...enquiry, whatsapp: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-violet-600 focus:ring-1 focus:ring-violet-600 outline-none"
                  />
                </div>
                <input
                  type="email"
                  required
                  placeholder="Email"
                  value={enquiry.email}
                  onChange={(e) => setEnquiry({ ...enquiry, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-violet-600 focus:ring-1 focus:ring-violet-600 outline-none"
                />
                {enquiryStatus === 'error' && <p className="text-red-600 text-sm text-left">{enquiryError}</p>}
                <button
                  type="submit"
                  disabled={enquiryStatus === 'submitting'}
                  className="w-full py-4 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 transition-all shadow-lg shadow-green-900/20 disabled:opacity-60"
                >
                  {enquiryStatus === 'submitting' ? 'Please wait…' : '💬 Send me the project list on WhatsApp'}
                </button>
                <p className="text-xs text-gray-400">No spam, no calls — just the curriculum and answers to your questions.</p>
              </form>
            )}
          </div>
        </section>
      )}

      {/* ── How the guidance works ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-10">
            Stuck never lasts long here
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              ['1', 'Build hands-on from day one', 'No videos to binge first. Most projects run right in your browser; a few use VS Code — we walk you through the setup when you get there.'],
              ['2', 'Ask doubts, get answers', 'Professional: within 24 hours. Advance: under 2 hours in office hours, else next day.'],
              ['3', 'Meet your mentor 1-on-1', 'Personal sessions to review your work, unblock you, and plan what you build next.'],
              ['4', 'Ship your portfolio', 'Finish with real projects recruiters can run — plus months of Cubemate Ultimate to keep building.'],
            ].map(([n, title, text]) => (
              <div key={n} className="p-6 rounded-2xl border border-gray-200 bg-white">
                <div className="w-9 h-9 rounded-full bg-violet-600 text-white font-bold flex items-center justify-center mb-4">{n}</div>
                <h3 className="text-lg font-bold mb-2">{title}</h3>
                <p className="text-gray-600 text-sm">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Meet your mentor — founder credibility ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-10">Meet your mentor</h2>
          <div className="p-8 rounded-2xl border border-gray-200 bg-white shadow-sm flex flex-col sm:flex-row items-center sm:items-start gap-6">
            {/* Initials avatar shows until /founder-francis.jpg is added to public/ */}
            <div className="relative w-24 h-24 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <span className="text-white text-3xl font-bold">FA</span>
              <img
                src="/founder-francis.png"
                alt="Francis Antony"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
            <div className="text-center sm:text-left">
              <h3 className="text-xl font-bold">Francis Antony</h3>
              <p className="text-violet-600 font-semibold text-sm mb-3">Founder, Cubemate · Corestrat AI</p>
              <p className="text-gray-600 text-sm mb-4">
                Francis built Cubemate — the AI learning platform every project in this course runs on — and
                mentors each batch personally. Your doubts and 1-on-1 sessions go to the person who built the
                product, not a support queue.
              </p>
              <a
                href={SOCIAL.founderLinkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:border-[#0a66c2] hover:text-[#0a66c2] transition-colors"
              >
                <LinkedInIcon />
                Connect on LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" id="pricing">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-10">Choose your level of guidance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch">
            {(['professional', 'advance'] as BuildTierKey[]).map((key) => {
              const t = cfg.tiers[key];
              return (
                <div
                  key={key}
                  className={`relative p-8 rounded-2xl border bg-white flex flex-col ${
                    t.highlight ? 'border-violet-600 shadow-xl shadow-violet-600/10' : 'border-gray-200 shadow-sm'
                  }`}
                >
                  {t.highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-violet-600 text-white text-xs font-bold uppercase tracking-wide">
                      Most popular
                    </span>
                  )}
                  <h3 className="text-2xl font-bold">{t.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{t.tagline}</p>
                  <p className="text-4xl font-extrabold mb-6">
                    {t.priceDisplay} <span className="text-base font-medium text-gray-400">one-time</span>
                  </p>
                  <ul className="space-y-3 mb-8 flex-1">
                    {t.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <svg className="w-5 h-5 text-violet-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-600 text-sm">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => chooseTier(key)}
                    className={`w-full py-3 rounded-xl font-bold transition-all ${
                      t.highlight
                        ? 'bg-violet-600 text-white hover:bg-violet-700 shadow-lg shadow-violet-600/25'
                        : 'border-2 border-violet-600 text-violet-700 hover:bg-violet-50'
                    }`}
                  >
                    Join {t.name}
                  </button>
                  {whatsappUrl && (
                    <p className="text-center text-sm text-gray-500 mt-3">
                      Not sure yet?{' '}
                      <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={trackContact} className="text-green-600 font-semibold hover:underline">
                        Ask us on WhatsApp first
                      </a>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Order form ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50" id="join">
        <div className="max-w-lg mx-auto">
          <h2 className="text-3xl font-bold text-center mb-2">Reserve your seat</h2>
          <p className="text-gray-500 text-center mb-8">{startLine}.</p>
          <form onSubmit={submit} className="p-8 rounded-2xl border border-gray-200 bg-white shadow-sm space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {(['professional', 'advance'] as BuildTierKey[]).map((key) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => setTier(key)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    tier === key ? 'border-violet-600 bg-violet-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-bold text-sm">{cfg.tiers[key].name}</p>
                  <p className="text-gray-500 text-sm">{cfg.tiers[key].priceDisplay}</p>
                </button>
              ))}
            </div>
            <input
              type="text"
              required
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-violet-600 focus:ring-1 focus:ring-violet-600 outline-none"
            />
            <input
              type="email"
              required
              placeholder="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-violet-600 focus:ring-1 focus:ring-violet-600 outline-none"
            />
            <input
              type="tel"
              required
              minLength={10}
              placeholder="WhatsApp number (required — we onboard you there)"
              value={form.whatsapp}
              onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-violet-600 focus:ring-1 focus:ring-violet-600 outline-none"
            />
            {status === 'error' && <p className="text-red-600 text-sm">{errorMsg}</p>}
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="w-full py-4 rounded-xl bg-violet-600 text-white font-bold hover:bg-violet-700 transition-all shadow-lg shadow-violet-600/25 disabled:opacity-60"
            >
              {status === 'submitting' ? 'Please wait…' : `Continue to payment — ${cfg.tiers[tier].priceDisplay}`}
            </button>
            <p className="text-xs text-gray-400 text-center">Secure payment via Razorpay.</p>
          </form>
          {whatsappUrl && (
            <p className="text-sm text-gray-500 text-center mt-4">
              Not sure which plan fits?{' '}
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-green-600 font-semibold hover:underline">
                Ask us on WhatsApp
              </a>
            </p>
          )}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-10">Questions, answered</h2>
          <div className="space-y-4">
            {[
              ['When does the course start?', cfg.mode === 'cohort' && cfg.cohortStartDate ? `The next batch starts ${cfg.cohortStartDate}.` : 'The day you join — no waiting for a batch.'],
              ['What if I get stuck?', 'That\'s the point of this course. Ask your doubt and get an answer within 24 hours on Professional, or under 2 hours during office hours on Advance — plus your 1-on-1 mentor sessions.'],
              ['Do I need a powerful laptop or any setup?', 'Any regular laptop works — no powerful machine needed. Most projects run right in your browser; a few use VS Code (free), and we\'ll help you set it up when you get there.'],
              ['What do I keep after the course?', 'All your project code, plus Cubemate Ultimate access (3 months on Professional, 1 year on Advance) to keep building.'],
              ['Who is this for?', 'Anyone who can write some code and wants real AI projects on their resume — students, career switchers, and working developers.'],
            ].map(([q, a]) => (
              <div key={q} className="p-6 rounded-2xl border border-gray-200 bg-white">
                <h3 className="font-bold mb-2">{q}</h3>
                <p className="text-gray-600 text-sm">{a}</p>
              </div>
            ))}
          </div>
          {whatsappUrl && (
            <p className="text-center mt-8 text-gray-600">
              Have a different question?{' '}
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-green-600 font-semibold hover:underline">
                Message us on WhatsApp
              </a>{' '}
              — we reply fast.
            </p>
          )}
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            When this is done, <span className="text-violet-600">you'll have the projects.</span>
          </h2>
          <p className="text-gray-600 mb-8">Or another saved post about learning AI. Your call.</p>
          <button
            onClick={() => chooseTier('professional')}
            className="px-8 py-4 text-lg font-bold rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition-all shadow-lg shadow-violet-600/25 hover:scale-105 transform"
          >
            Join now — {cfg.tiers.professional.priceDisplay}
          </button>
        </div>
      </section>

      {/* ── Floating WhatsApp bubble ── */}
      {whatsappUrl && (
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={trackContact}
          aria-label="Chat with us on WhatsApp"
          className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-green-500 hover:bg-green-600 shadow-lg shadow-green-500/30 flex items-center justify-center transition-all hover:scale-110"
        >
          <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </a>
      )}

      {/* ── Footer ── */}
      <footer className="py-8 px-4 border-t border-gray-200">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <img src="/cubemate-logo.png" alt="Cubemate" className="w-6 h-6 rounded" />
            <span>© {new Date().getFullYear()} Cubemate</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            <a href={SOCIAL.corestratLinkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#0a66c2] transition-colors">
              <LinkedInIcon /> CorestratAI Solutions
            </a>
            <a href={SOCIAL.corestratInstagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#E4405F] transition-colors">
              <InstagramIcon /> CorestratAI Solutions
            </a>
            <a href={SOCIAL.cubemateInstagram} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-[#E4405F] transition-colors">
              <InstagramIcon /> Cubemate AI
            </a>
            <a href="/privacy" className="hover:text-gray-900 transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-gray-900 transition-colors">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BuildCoursePage;
